import React from 'react';
import type { Asset } from '../../../api/models/asset.model.ts';
import ModifyUserDefinedFieldsModal from './ModifyUserDefinedFieldsModal.tsx';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateLeasedUdfValuesMutation } from '../../../api/asset.api.ts';
import { showAlert } from '../../../redux/slices/alertSlice.ts';
import { selectAccount } from '../../../redux/slices/accountSlice.ts';
import {
	acknowledged,
	bumpAssetGridRefresh,
	selectLeasedUdfInProgress,
	setUploadInProgress,
} from '../../../redux/slices/udfUpdateSlice.ts';

interface ModifyLeasedUserDefinedFieldsModalProps {
	open: boolean;
	onClose: () => void;
	selectedRows: Asset[];
}

function ModifyLeasedUserDefinedFieldsModal({ open, onClose, selectedRows }: ModifyLeasedUserDefinedFieldsModalProps) {
	const dispatch = useDispatch();
	const selectedAccount = useSelector(selectAccount);
	const [ updateLeasedUdfValue ] = useUpdateLeasedUdfValuesMutation();
	const uploadInProgress = useSelector(selectLeasedUdfInProgress);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const handleClose = () => {
		onClose();
	};

	const callUpdateUdfValues = async (udfValues: Record<string, string>) => {
		try {
			dispatch(setUploadInProgress());
			setIsSubmitting(true);
			await updateLeasedUdfValue({
				assets: selectedRows,
				udfValues,
				account: selectedAccount.accountId,
			}).unwrap();

			dispatch(bumpAssetGridRefresh());

			dispatch(showAlert({
				message: 'Updated successfully',
				type: 'success',
			}));
			
			onClose();
		} catch (error) {
			dispatch(acknowledged());
			dispatch(showAlert({
				message: 'Update failed. Please try again.',
				type: 'error',
			}));
		} finally {
			setIsSubmitting(false);
		}
	}

	if (!open) return null;

	return (<ModifyUserDefinedFieldsModal
		open={open}
		onClose={handleClose}
		selectedRows={selectedRows}
		callUpdateUdfValues={callUpdateUdfValues}
		downstreamCallInProgress={uploadInProgress || isSubmitting}
	/>)
}

export default ModifyLeasedUserDefinedFieldsModal;